import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader, StatusBadge } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { INSTALL_STATUS } from "@/lib/labels";
import { toast } from "sonner";

export default function AdminInstallations() {
  const [items, setItems] = useState(null);
  const [pros, setPros] = useState([]);
  const [filter, setFilter] = useState("all");
  const [assign, setAssign] = useState(null);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    const [i, p] = await Promise.all([api.get("/installations"), api.get("/admin/professionals", { params: { role: "professional" } })]);
    setItems(i.data); setPros(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!items) return <Loader />;
  const filtered = filter === "urgent" ? items.filter((i) => i.urgency === "urgent")
    : filter === "todo" ? items.filter((i) => ["demande"].includes(i.status))
    : filter === "tovalidate" ? items.filter((i) => i.status === "terminee")
    : filter === "refused" ? items.filter((i) => i.status === "refusee")
    : items;

  const validate = async (iid) => {
    try { await api.put(`/installations/${iid}/status`, null, { params: { status: "validee" } }); toast.success("Intervention validée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageTitle>Installations</PageTitle>
      <div className="flex gap-2 mb-4 flex-wrap">
        {[["all", "Toutes"], ["todo", "À affecter"], ["tovalidate", "À valider"], ["refused", "Refusées"], ["urgent", "Urgentes"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`install-filter-${v}`} className={`px-4 py-2 rounded-lg text-sm font-semibold ${filter === v ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.map((ins) => (
          <Panel key={ins.id} className="p-4" data-testid={`admin-install-${ins.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-num font-bold">{ins.mission_number}</span>
              <StatusBadge map={INSTALL_STATUS} value={ins.status} />
              {ins.urgency === "urgent" && <span className="bg-red-100 text-red-700 text-[11px] font-bold px-2 py-0.5 rounded-full">⚡ URGENT</span>}
              <span className="text-xs text-slate-400 ml-auto">{formatDate(ins.created_at)}</span>
            </div>
            <p className="text-sm font-semibold mt-1">{ins.product_name} — {ins.customer_name}</p>
            <p className="text-xs text-slate-400">{ins.skill} · {ins.zone} · {ins.address} {ins.scheduled_date && `· ${formatDate(ins.scheduled_date)} ${ins.scheduled_time || ""}`}</p>
            {ins.status === "refusee" && <p className="text-xs text-red-600 mt-1 font-semibold" data-testid={`refusal-${ins.id}`}>Refusée par {ins.professional_name} — Motif : {ins.refusal_reason || "non précisé"}</p>}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {ins.professional_name && ins.status !== "refusee" ? <span className="text-sm text-slate-600">Technicien : <strong>{ins.professional_name}</strong> · Pro: {formatFCFA(ins.pro_amount)} · Client: {formatFCFA(ins.client_amount)}</span>
                : ins.status === "refusee" ? <span className="text-sm text-red-600 font-semibold">À réaffecter</span>
                : <span className="text-sm text-amber-600 font-semibold">Non affectée</span>}
              <div className="ml-auto flex gap-2">
                {(["before_photos", "during_photos", "after_photos"].some((k) => (ins[k] || []).length) || ins.difficulties || ins.client_signature) && <button onClick={() => setReport(ins)} className="bg-white border border-slate-200 text-slate-700 text-sm font-bold px-3 py-1.5 rounded-lg" data-testid={`report-btn-${ins.id}`}>Rapport</button>}
                {ins.status === "terminee" && <button onClick={() => validate(ins.id)} className="bg-green-600 text-white text-sm font-bold px-3 py-1.5 rounded-lg" data-testid={`validate-btn-${ins.id}`}>Valider</button>}
                <button onClick={() => setAssign(ins)} className="bg-[#0F172A] text-white text-sm font-bold px-3 py-1.5 rounded-lg" data-testid={`assign-btn-${ins.id}`}>
                  {ins.professional_name && ins.status !== "refusee" ? "Réaffecter" : "Affecter"}
                </button>
              </div>
            </div>
          </Panel>
        ))}
      </div>
      {assign && <AssignModal ins={assign} pros={pros} onClose={() => setAssign(null)} onDone={load} />}
      {report && <ReportModal ins={report} onClose={() => setReport(null)} />}
    </div>
  );
}

function ReportModal({ ins, onClose }) {
  const groups = [["avant", "before_photos"], ["pendant", "during_photos"], ["après", "after_photos"]];
  const hasAny = groups.some(([, k]) => (ins[k] || []).length) || ins.difficulties || ins.extra_material || ins.client_signature;
  return (
    <Modal title={`Rapport — ${ins.mission_number}`} onClose={onClose} testid="report-modal" size="lg">
      <div className="space-y-4" data-testid="report-content">
        {groups.map(([label, key]) => (ins[key] || []).length > 0 && (
          <div key={key}>
            <p className="text-sm font-semibold text-slate-700 mb-1.5">Photos {label}</p>
            <div className="flex flex-wrap gap-2">{(ins[key] || []).map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" className="w-24 h-24 object-cover rounded-lg border border-slate-200" /></a>
            ))}</div>
          </div>
        ))}
        {ins.difficulties && <div><p className="text-sm font-semibold text-slate-700">Difficultés rencontrées</p><p className="text-sm text-slate-600">{ins.difficulties}</p></div>}
        {ins.extra_material && <div><p className="text-sm font-semibold text-slate-700">Matériel supplémentaire</p><p className="text-sm text-slate-600">{ins.extra_material}</p></div>}
        {ins.client_signature && <div><p className="text-sm font-semibold text-slate-700 mb-1">Signature client</p><img src={ins.client_signature} alt="signature" className="max-w-xs border border-slate-200 rounded-lg" /></div>}
        {!hasAny && <p className="text-sm text-slate-400">Aucun rapport transmis pour le moment.</p>}
      </div>
    </Modal>
  );
}

function AssignModal({ ins, pros, onClose, onDone }) {
  const [f, setF] = useState({
    professional_id: ins.professional_id || "", pro_amount: ins.pro_amount || 0,
    client_amount: ins.client_amount || 0, scheduled_date: ins.scheduled_date || "", scheduled_time: ins.scheduled_time || "",
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const matching = pros.filter((p) => !ins.skill || p.skills?.some((s) => ins.skill.toLowerCase().includes(s) || s.includes(ins.skill.toLowerCase())) || p.zones?.includes(ins.zone));

  const submit = async (e) => {
    e.preventDefault();
    try { await api.put(`/installations/${ins.id}/assign`, { ...f, pro_amount: Number(f.pro_amount), client_amount: Number(f.client_amount) }); toast.success("Mission affectée"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title={`Affecter ${ins.mission_number}`} onClose={onClose} testid="assign-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-500">Compétence requise : <strong>{ins.skill}</strong> · Zone : <strong>{ins.zone}</strong></p>
        <SelectField label="Professionnel" value={f.professional_id} onChange={set("professional_id")} testid="assign-pro"
          options={(matching.length ? matching : pros).map((p) => ({ value: p.user_id, label: `${p.name} (${(p.skills || []).join(", ")})` }))} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Montant client (FCFA)" type="number" value={f.client_amount} onChange={set("client_amount")} testid="assign-client-amount" />
          <TextField label="Montant pro (FCFA)" type="number" value={f.pro_amount} onChange={set("pro_amount")} testid="assign-pro-amount" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Date" type="date" value={f.scheduled_date} onChange={set("scheduled_date")} testid="assign-date" required={false} />
          <TextField label="Heure" type="time" value={f.scheduled_time} onChange={set("scheduled_time")} testid="assign-time" required={false} />
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="assign-submit">Affecter la mission</button>
      </form>
    </Modal>
  );
}
